import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import {
  RocketLaunch as RocketLaunchIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Subscriptions as SubscriptionsIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import DeploymentRow from './DeploymentRow';
import SubscribePanel from './SubscribePanel';
import SubscribedOfferingsTabContent from './SubscribedOfferingsTabContent';
import LandscapeView from '../../landscape/components/LandscapeView';
import { useCustomerDeployments } from '../hooks/useCustomerDeployments';
import { useDeploymentList } from '../hooks/useDeploymentList';
import { useServiceComponentDependencies } from '../hooks/useServiceComponentDependencies';
import { useCatalogService, type ServiceComponentSummary } from '../services/catalogService';
import { useAwsAccountService } from '../../aws-account/services/awsAccountService';
import { rowConfigFor, ATTENTION_STATES, RECONCILING_STATES, type DerivedState } from '../rowConfig';
import type { AwsAccount, Deployment } from '../../../types/backend';

interface CustomerDeploymentsModalProps {
  customerId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

type FilterKey = 'attention' | 'reconciling' | 'sync' | 'acknowledged' | 'unsubscribed' | 'deprecated' | 'all';

function deploymentMatchesFilter(
  state: DerivedState,
  deprecated: boolean,
  filter: FilterKey,
): boolean {
  switch (filter) {
    case 'all': return true;
    case 'attention': return ATTENTION_STATES.includes(state);
    case 'reconciling': return RECONCILING_STATES.includes(state);
    case 'sync': return state === 'in-sync';
    case 'acknowledged': return state === 'acknowledged-drift';
    case 'unsubscribed': return state === 'unsubscribed';
    case 'deprecated': return deprecated;
  }
}

const CustomerDeploymentsModal = memo<CustomerDeploymentsModalProps>(({ customerId, isOpen, onClose }) => {
  const { customer } = useCustomerDeployments({ customerId, isOpen });
  const {
    deployments,
    loading,
    error,
    refresh,
  } = useDeploymentList(isOpen ? customerId : undefined);
  const catalogService = useCatalogService();
  const awsAccountService = useAwsAccountService();

  const [products, setServiceComponents] = useState<ServiceComponentSummary[]>([]);
  const [awsAccounts, setAwsAccounts] = useState<AwsAccount[]>([]);
  const [filter, setFilter] = useState<FilterKey>('attention');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'list' | 'subscribe'>('list');
  const [deploymentView, setDeploymentView] = useState<'list' | 'landscape'>('list');
  const [activeTab, setActiveTab] = useState(0);

  // Recent versions cache, keyed by product_id
  const [versionsCache, setVersionsCache] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!isOpen) return;
    catalogService.listServiceComponents().then((result) => {
      setServiceComponents(result.products);
    }).catch(() => setServiceComponents([]));
  }, [isOpen, catalogService]);

  useEffect(() => {
    if (!isOpen || !customerId) return;
    awsAccountService.getCustomerAwsAccounts(customerId).then((r) => {
      setAwsAccounts(r.customer_accounts);
    }).catch(() => setAwsAccounts([]));
  }, [isOpen, customerId, awsAccountService]);

  useEffect(() => {
    if (!isOpen) {
      setFilter('attention');
      setSearch('');
      setView('list');
      setVersionsCache({});
      setAwsAccounts([]);
      setActiveTab(0);
    }
  }, [isOpen]);

  const productByName = useMemo(() => new Map(products.map((p) => [p.name, p])), [products]);
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const dependencies = useServiceComponentDependencies(deployments, products);

  const counts = useMemo(() => {
    const c: Record<FilterKey, number> = { attention: 0, reconciling: 0, sync: 0, acknowledged: 0, unsubscribed: 0, deprecated: 0, all: 0 };
    deployments.forEach((d) => {
      const product = productByName.get(d.product_name ?? '') ?? productById.get(d.product_id);
      const cfg = rowConfigFor(d, product, dependencies[d.id]);
      c.all += 1;
      if (ATTENTION_STATES.includes(cfg.state)) c.attention += 1;
      if (RECONCILING_STATES.includes(cfg.state)) c.reconciling += 1;
      if (cfg.state === 'in-sync') c.sync += 1;
      if (cfg.state === 'acknowledged-drift') c.acknowledged += 1;
      if (cfg.state === 'unsubscribed') c.unsubscribed += 1;
      if (cfg.identity.productDeprecated) c.deprecated += 1;
    });
    return c;
  }, [deployments, productByName, productById, dependencies]);

  const visibleDeployments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deployments.filter((d) => {
      const product = productByName.get(d.product_name ?? '') ?? productById.get(d.product_id);
      const cfg = rowConfigFor(d, product, dependencies[d.id]);
      if (!deploymentMatchesFilter(cfg.state, cfg.identity.productDeprecated, filter)) {
        return false;
      }
      if (!q) return true;
      return (
        (d.product_name ?? '').toLowerCase().includes(q) ||
        d.account_id.toLowerCase().includes(q) ||
        d.region.toLowerCase().includes(q)
      );
    });
  }, [deployments, productByName, productById, dependencies, filter, search]);

  const loadRecentVersions = useCallback(
    (productId: string, service: string) => {
      if (versionsCache[productId]) return;
      catalogService
        .listVersions(productId, service)
        .then((result) => {
          const names = [...result.versions]
            .sort((a, b) => (b.created_time ?? '').localeCompare(a.created_time ?? ''))
            .map((v) => v.name);
          setVersionsCache((prev) => ({ ...prev, [productId]: names }));
        })
        .catch(() => {
          setVersionsCache((prev) => ({ ...prev, [productId]: [] }));
        });
    },
    [catalogService, versionsCache],
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{ paper: { sx: { height: '85vh', maxHeight: '85vh' } } }}
    >
      <ModalHeader
        icon={RocketLaunchIcon}
        title="Deployments"
        onClose={onClose}
        desktopRecommended
        titleExtra={customer ? <Chip label={customer.name} size="small" variant="outlined" /> : undefined}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Deployments" icon={<ViewListIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label="Subscriptions" icon={<SubscriptionsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
        </Tabs>
      </Box>

      {activeTab === 0 && customerId && (
        <Box sx={{ px: 3, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <ToggleButtonGroup size="small" exclusive value={deploymentView} onChange={(_, v) => v && setDeploymentView(v)}>
            <ToggleButton value="list">List</ToggleButton>
            <ToggleButton value="landscape">Landscape</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
        {!customerId ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Select a customer to view deployments.</Typography>
          </Box>
        ) : activeTab === 1 ? (
          <SubscribedOfferingsTabContent customerId={customerId} isActive={isOpen && activeTab === 1} />
        ) : deploymentView === 'landscape' ? (
          <LandscapeView customerId={customerId} customerName={customer?.name} active={isOpen && activeTab === 0 && deploymentView === 'landscape'} />
        ) : loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search product, account, region…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 220, flex: 1 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
              {[
                { key: 'attention' as const, label: 'Needs action' },
                { key: 'reconciling' as const, label: 'Reconciling' },
                { key: 'sync' as const, label: 'In sync' },
                { key: 'acknowledged' as const, label: 'Acknowledged' },
                { key: 'unsubscribed' as const, label: 'Unsubscribed' },
                { key: 'deprecated' as const, label: 'Deprecated' },
                { key: 'all' as const, label: 'All' },
              ].map(({ key, label }) => (
                <Chip
                  key={key}
                  label={`${label} · ${counts[key]}`}
                  color={filter === key ? 'primary' : 'default'}
                  variant={filter === key ? 'filled' : 'outlined'}
                  size="small"
                  onClick={() => setFilter(key)}
                  sx={{ fontWeight: 500 }}
                />
              ))}
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setView('subscribe')}
                >
                  Subscribe a component
                </Button>
                <Button
                  size="small"
                  startIcon={<RefreshIcon fontSize="small" />}
                  onClick={() => refresh()}
                  sx={{ color: 'text.secondary' }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
              {visibleDeployments.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No deployments match the current filter.
                  </Typography>
                </Box>
              ) : (
                visibleDeployments.map((d: Deployment) => {
                  const product = productByName.get(d.product_name ?? '') ?? productById.get(d.product_id);
                  const productId = product?.id ?? d.product_id;
                  loadRecentVersions(productId, d.service);
                  return (
                    <DeploymentRow
                      key={d.id}
                      customerId={customerId}
                      deployment={d}
                      product={product}
                      dependencies={dependencies[d.id]}
                      recentVersions={versionsCache[productId] ?? []}
                      onRefresh={refresh}
                    />
                  );
                })
              )}


            </Box>
            </Box>
            {view === 'subscribe' && (
              <Box sx={{ width: 440, flexShrink: 0, display: 'flex' }}>
                <SubscribePanel
                  customerId={customerId}
                  awsAccounts={awsAccounts}
                  products={products}
                  onCancel={() => setView('list')}
                  onSubscribed={() => { refresh(); setView('list'); }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <ModalFooter
        count={deployments.length}
        label={`deployment${deployments.length !== 1 ? 's' : ''}`}
        onClose={onClose}
      />
    </Dialog>
  );
});

CustomerDeploymentsModal.displayName = 'CustomerDeploymentsModal';

export default CustomerDeploymentsModal;
